import { IconChevronRightSmall } from "@/assets/icons/geist/IconChevronRightSmall";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Counter } from "@/components/flexkit/badge/Counter";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { computeCollapsedIndices } from "@/components/breadcrumb-collapse";
import { cn } from "@/components/podkit/lib/cn";
import { useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { Link, useMatches, useNavigate, type UIMatch } from "react-router-dom";
import type { BreadcrumbItem } from "@/contexts/SettingsPageContext";

type BreadcrumbsProps = {
    label?: string | ReactNode;
    customBreadcrumbs?: BreadcrumbItem[] | null;
    size?: "default" | "large";
};

// Width of the collapsed "..." element per size variant.
//   default: icon (size-6 = 24px) + chevron (base = 20px) + gaps ≈ 44px
//   large:   icon (size-8 = 32px) + chevron (lg = 24px) + gaps ≈ 68px
// Update these if icon sizes, chevron sizes, or gap values change.
const ELLIPSIS_WIDTH_PX = { default: 44, large: 68 } as const;

/**
 * Measures a hidden row that always renders every item at natural width
 * and determines which non-last items to collapse to "...".
 *
 * The hidden row never changes based on collapsed state, so there is no
 * measure → collapse → re-measure feedback loop.
 */
function useCollapsedIndices(itemCount: number, size: "default" | "large") {
    const measureRef = useRef<HTMLOListElement>(null);
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
    const ellipsisWidth = ELLIPSIS_WIDTH_PX[size];

    const check = useCallback(() => {
        const ol = measureRef.current;
        if (!ol || itemCount < 2) {
            setCollapsed((prev) => (prev.size === 0 ? prev : new Set()));
            return;
        }

        const containerWidth = ol.clientWidth;
        const items = Array.from(ol.querySelectorAll<HTMLElement>(":scope > li"));
        if (items.length === 0) return;

        const naturalWidths = items.map((li) => li.scrollWidth);
        const next = computeCollapsedIndices(naturalWidths, containerWidth, ellipsisWidth);

        setCollapsed((prev) => {
            if (prev.size !== next.size) return next;
            for (const v of prev) {
                if (!next.has(v)) return next;
            }
            return prev;
        });
    }, [itemCount, ellipsisWidth]);

    useEffect(() => {
        const ol = measureRef.current;
        if (!ol) return;
        check();
        const observer = new ResizeObserver(check);
        observer.observe(ol);
        return () => observer.disconnect();
    }, [check]);

    return { measureRef, collapsed };
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const titleFor = (nodeLabel: ReactNode) => (typeof nodeLabel === "string" ? nodeLabel : undefined);

type ChevronProps = { isLarge: boolean };

const Chevron: FC<ChevronProps> = ({ isLarge }) => (
    <IconChevronRightSmall size={isLarge ? "lg" : "base"} className="shrink-0 text-content-inactive" />
);

type CollapsedItemProps = {
    collapsedItems: BreadcrumbItem[];
    tooltip: string | undefined;
    isLarge: boolean;
};

const CollapsedBreadcrumbItem: FC<CollapsedItemProps> = ({ collapsedItems, tooltip, isLarge }) => {
    const triggerClasses = cn(
        "flex items-center justify-center rounded text-content-secondary hover:bg-surface-tertiary hover:text-content-primary",
        isLarge ? "size-8" : "size-6",
    );

    return (
        <li className={cn("flex shrink-0 flex-row items-center", isLarge ? "gap-2" : "gap-0.5")}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className={triggerClasses}
                        aria-label="Show collapsed breadcrumbs"
                        title={tooltip}
                    >
                        <IconEllipsisHorizontal size={isLarge ? "base" : "sm"} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-auto min-w-[160px] max-w-[300px]">
                    {collapsedItems.map((item, i) => (
                        <DropdownMenuItem key={i} asChild>
                            {item.to ? (
                                <Link to={item.to} className="truncate">
                                    {item.label}
                                </Link>
                            ) : (
                                <span className="truncate">{item.label}</span>
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <Chevron isLarge={isLarge} />
        </li>
    );
};

type CurrentPageItemProps = {
    crumb: BreadcrumbItem;
    isLarge: boolean;
};

const CurrentPageItem: FC<CurrentPageItemProps> = ({ crumb, isLarge }) => {
    const Icon = crumb.icon;
    return (
        <li
            className={cn(
                "flex min-w-0 shrink items-center text-content-primary",
                isLarge ? "gap-2 text-2xl font-semibold tracking-[-0.2px]" : "gap-1.5 text-base font-medium",
            )}
        >
            {Icon && <Icon className={cn("shrink-0", isLarge ? "size-6" : "size-4")} />}
            <span className="truncate" title={titleFor(crumb.label)}>
                {crumb.label}
            </span>
            {crumb.description && <Counter counter={crumb.description} />}
        </li>
    );
};

type LinkItemProps = {
    crumb: BreadcrumbItem;
    isLarge: boolean;
};

const LinkBreadcrumbItem: FC<LinkItemProps> = ({ crumb, isLarge }) => {
    const Icon = crumb.icon;
    return (
        <li
            className={cn(
                "flex min-w-0 shrink-0 flex-row items-center text-content-strong",
                isLarge ? "gap-[2px] text-2xl font-normal tracking-[-0.2px]" : "gap-0.5 text-base font-normal",
            )}
        >
            {Icon && <Icon className={cn("shrink-0", isLarge ? "size-6" : "size-4")} />}
            <Link
                to={crumb.to!}
                className="max-w-[200px] truncate hover:text-content-primary"
                title={titleFor(crumb.label)}
            >
                {crumb.label}
            </Link>
            <Chevron isLarge={isLarge} />
        </li>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Breadcrumbs: FC<BreadcrumbsProps> = ({ label, customBreadcrumbs, size = "default" }) => {
    const matches = useMatches() as UIMatch<unknown, { label: string; pathname: string; description?: string }>[];
    const crumbs = matches.filter((match) => Boolean(match.handle));
    const parents = crumbs.slice(0, -1);
    const current = crumbs[crumbs.length - 1];
    const navigate = useNavigate();
    const pathOfParentPage = parents.length > 0 && parents[parents.length - 1].pathname;

    useEffect(() => {
        if (!pathOfParentPage) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (document.activeElement?.nodeName !== "BODY") return;
            if (e.key === "Escape") {
                e.stopImmediatePropagation();
                e.stopPropagation();
                e.preventDefault();
                navigate(pathOfParentPage);
            }
        };
        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () => window.removeEventListener("keydown", handleKeyDown, { capture: true });
    }, [navigate, pathOfParentPage, current]);

    let finalBreadcrumbs: BreadcrumbItem[] = [];
    const parentsWithLabels = parents
        .filter((p) => Boolean(p.handle?.label))
        .map((p) => ({ label: p.handle?.label, to: p.pathname }));

    if (customBreadcrumbs && customBreadcrumbs.length > 0) {
        finalBreadcrumbs = [...parentsWithLabels, ...customBreadcrumbs];
    } else {
        finalBreadcrumbs = [...parentsWithLabels, { label: current?.handle?.label ?? label }];
    }

    const isLarge = size === "large";
    const { measureRef, collapsed } = useCollapsedIndices(finalBreadcrumbs.length, size);

    const collapsedItems = finalBreadcrumbs.filter((_, i) => collapsed.has(i));
    const ellipsisTooltip = collapsedItems
        .map((item) => (typeof item.label === "string" ? item.label : ""))
        .filter(Boolean)
        .join(" / ");

    // Fixed height prevents layout shift when items collapse to "..."
    const olClassName = cn(
        "flex min-w-0 flex-row items-center",
        isLarge ? "h-9 gap-2 text-2xl" : "h-6 gap-0.5 text-base",
    );

    return (
        <div className="relative min-w-0">
            {/* Hidden measurement row — renders all items at natural width (shrink-0)
                with max-w-[200px] on non-last items. Must mirror the visible row's
                icon sizes, gaps, and chevron sizing. */}
            <ol ref={measureRef} className={cn(olClassName, "invisible absolute inset-0 overflow-hidden")} aria-hidden>
                {finalBreadcrumbs.map((crumb, index) => {
                    const isLast = index === finalBreadcrumbs.length - 1;
                    return (
                        <li key={index} className="flex shrink-0 flex-row items-center whitespace-nowrap">
                            {crumb.icon && (
                                <crumb.icon
                                    className={cn("shrink-0", isLarge ? "mr-2 size-6" : "mr-1.5 size-4")}
                                />
                            )}
                            <span className={isLast ? undefined : "max-w-[200px] truncate"}>
                                {crumb.label}
                            </span>
                            {!isLast && (
                                <IconChevronRightSmall
                                    size={isLarge ? "lg" : "base"}
                                    className={cn("shrink-0", isLarge ? "mx-2" : "mx-0.5")}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>

            {/* Visible breadcrumbs */}
            <ol className={olClassName}>
                {finalBreadcrumbs.map((crumb, index) => {
                    const isLast = index === finalBreadcrumbs.length - 1;

                    if (collapsed.has(index)) {
                        // Skip consecutive collapsed items — they merge into one "..."
                        if (index > 0 && collapsed.has(index - 1)) return null;
                        return (
                            <CollapsedBreadcrumbItem
                                key={`ellipsis-${index}`}
                                collapsedItems={collapsedItems}
                                tooltip={ellipsisTooltip || undefined}
                                isLarge={isLarge}
                            />
                        );
                    }

                    if (isLast || !crumb.to) {
                        return <CurrentPageItem key={index} crumb={crumb} isLarge={isLarge} />;
                    }

                    return <LinkBreadcrumbItem key={index} crumb={crumb} isLarge={isLarge} />;
                })}
            </ol>
        </div>
    );
};

export default Breadcrumbs;
